import { Directive, ElementRef, HostListener, inject } from '@angular/core';

// Marks an element as an interactive surface — a clickable card, a list row. It carries the
// shared interactive-state treatment (styles/interactive.scss, per states.mdx § 2) and makes
// a non-native element keyboard-operable, so a clickable card is never mouse-only. The click
// itself stays the consumer's — a routerLink or (click) — this only dresses and enables it.
@Directive({
    selector: '[rad-interactive]',
    standalone: true,
    host: {
        class: 'rad-interactive',
    },
})
export class RadInteractiveDirective {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    constructor() {
        // Only give focus to something that hasn't got it — a native control, or an element the
        // consumer already gave a tabindex, is left alone.
        if (!this.host.matches('a, button, input, select, textarea, [tabindex]')) {
            this.host.setAttribute('tabindex', '0');
        }
    }

    @HostListener('keydown', ['$event'])
    activateOnKey(event: KeyboardEvent): void {
        // A plain element does not fire click on Enter/Space, so we do — and the [tabindex] we
        // added above must NOT count here, or we'd skip the very element we made focusable.
        if (this.firesClickNatively() || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }
        event.preventDefault();
        this.host.click();
    }

    private firesClickNatively(): boolean {
        return this.host.matches('a, button, input, select, textarea');
    }
}
